/**
 * Wire-контракт замера «Похожие варианты» (`Platform.Contracts/Testing/TwinTestingContracts.cs`, RP-FB67) и его проверка на границе: неожиданная форма —
 * `invalid-response`, а не пустой экран.
 */
import { fail, flag, integer, list, optionalText, record, text } from '../../../shared/decode'
import type {
  TwinConfusion,
  TwinFamilies,
  TwinFamily,
  TwinFrameAdded,
  TwinPriority,
  TwinRun,
  TwinRunDetail,
  TwinRunMetrics,
  TwinRunStamp,
  TwinSeriesSlice,
  TwinVariant,
} from '../model/twinTypes'
import { isTwinPriority } from '../model/twinTypes'
import { isTestingRunState } from '../model/types'
import { anyText, decodeComparison, mean, optionalNum, proportion } from './contracts'

function priority(value: unknown, field: string): TwinPriority {
  return typeof value === 'string' && isTwinPriority(value) ? value : fail(`поле ${field} — неизвестный приоритет`)
}

function decodeVariant(raw: unknown, name: string): TwinVariant {
  const item = record(raw, name)
  return {
    catalogProductId: text(item.catalogProductId, `${name}.catalogProductId`),
    name: anyText(item.name, `${name}.name`),
    hasPhoto: flag(item.hasPhoto, `${name}.hasPhoto`),
    differences: anyText(item.differences, `${name}.differences`),
    testProductId: optionalText(item.testProductId, `${name}.testProductId`),
    frames: integer(item.frames, `${name}.frames`),
  }
}

function decodeFamily(raw: unknown, name: string): TwinFamily {
  const item = record(raw, name)
  return {
    key: text(item.key, `${name}.key`),
    series: anyText(item.series, `${name}.series`),
    priority: priority(item.priority, `${name}.priority`),
    withPhoto: integer(item.withPhoto, `${name}.withPhoto`),
    variants: list(item.variants, `${name}.variants`, decodeVariant),
  }
}

export function decodeTwinFamilies(raw: unknown): TwinFamilies {
  const body = record(raw, 'families')
  return {
    families: list(body.families, 'families.families', decodeFamily),
    total: integer(body.total, 'families.total'),
    catalogProducts: integer(body.catalogProducts, 'families.catalogProducts'),
    builtAt: text(body.builtAt, 'families.builtAt'),
  }
}

export function decodeTwinFrameAdded(raw: unknown): TwinFrameAdded {
  const body = record(raw, 'frameAdded')
  const product = record(body.product, 'frameAdded.product')
  return {
    testProductId: text(product.id, 'frameAdded.product.id'),
    frames: integer(product.frames, 'frameAdded.product.frames'),
    references: integer(product.references, 'frameAdded.product.references'),
  }
}

function decodeSeriesSlice(raw: unknown, name: string): TwinSeriesSlice {
  const item = record(raw, name)
  return {
    series: anyText(item.series, `${name}.series`),
    frames: integer(item.frames, `${name}.frames`),
    confused: proportion(item.confused, `${name}.confused`),
    choiceOffered: proportion(item.choiceOffered, `${name}.choiceOffered`),
    foundCorrect: proportion(item.foundCorrect, `${name}.foundCorrect`),
  }
}

function decodeConfusion(raw: unknown, name: string): TwinConfusion {
  const item = record(raw, name)
  return {
    expectedName: anyText(item.expectedName, `${name}.expectedName`),
    insteadName: anyText(item.insteadName, `${name}.insteadName`),
    count: integer(item.count, `${name}.count`),
  }
}

function decodeMetrics(raw: unknown): TwinRunMetrics {
  const body = record(raw, 'metrics')
  const latency = record(body.latency, 'metrics.latency')
  return {
    confused: proportion(body.confused, 'metrics.confused'),
    topIsTwin: proportion(body.topIsTwin, 'metrics.topIsTwin'),
    choiceOffered: proportion(body.choiceOffered, 'metrics.choiceOffered'),
    foundVerdict: proportion(body.foundVerdict, 'metrics.foundVerdict'),
    foundCorrect: proportion(body.foundCorrect, 'metrics.foundCorrect'),
    photoFixedText: proportion(body.photoFixedText, 'metrics.photoFixedText'),
    photoSpoiledText: proportion(body.photoSpoiledText, 'metrics.photoSpoiledText'),
    mrr: mean(body.mrr, 'metrics.mrr'),
    series: list(body.series, 'metrics.series', decodeSeriesSlice),
    confusions: list(body.confusions, 'metrics.confusions', decodeConfusion),
    latency: {
      n: integer(latency.n, 'metrics.latency.n'),
      medianMs: optionalNum(latency.medianMs, 'metrics.latency.medianMs'),
      p95Ms: optionalNum(latency.p95Ms, 'metrics.latency.p95Ms'),
    },
    twinFrames: integer(body.twinFrames, 'metrics.twinFrames'),
    otherFrames: integer(body.otherFrames, 'metrics.otherFrames'),
    errors: integer(body.errors, 'metrics.errors'),
  }
}

function decodeStamp(raw: unknown): TwinRunStamp {
  const body = record(raw, 'stamp')
  return {
    verdict: text(body.verdict, 'stamp.verdict'),
    confidenceFormula: text(body.confidenceFormula, 'stamp.confidenceFormula'),
    photoPrep: text(body.photoPrep, 'stamp.photoPrep'),
    platformVersion: text(body.platformVersion, 'stamp.platformVersion'),
    encoderModel: optionalText(body.encoderModel, 'stamp.encoderModel'),
    scope: text(body.scope, 'stamp.scope'),
    frames: integer(body.frames, 'stamp.frames'),
    families: integer(body.families, 'stamp.families'),
    variants: integer(body.variants, 'stamp.variants'),
    twinPhotoCoverage: optionalNum(body.twinPhotoCoverage, 'stamp.twinPhotoCoverage'),
  }
}

function decodeRunBody(raw: unknown, name = 'twinRun'): TwinRun {
  const item = record(raw, name)
  const state = text(item.state, `${name}.state`)
  if (!isTestingRunState(state)) fail(`поле ${name}.state — неизвестное состояние`)
  return {
    id: text(item.id, `${name}.id`),
    state,
    scope: text(item.scope, `${name}.scope`),
    createdAt: text(item.createdAt, `${name}.createdAt`),
    startedAt: optionalText(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
    totalFrames: integer(item.totalFrames, `${name}.totalFrames`),
    doneFrames: integer(item.doneFrames, `${name}.doneFrames`),
    error: optionalText(item.error, `${name}.error`),
    createdBy: anyText(item.createdBy, `${name}.createdBy`),
    stamp: item.stamp === null || item.stamp === undefined ? null : decodeStamp(item.stamp),
    metrics: item.metrics === null || item.metrics === undefined ? null : decodeMetrics(item.metrics),
  }
}

export function decodeTwinRun(raw: unknown): TwinRun {
  return decodeRunBody(raw)
}

export function decodeTwinRuns(raw: unknown): TwinRun[] {
  return list(raw, 'twinRuns', (entry, name) => decodeRunBody(entry, name))
}

export function decodeTwinRunDetail(raw: unknown): TwinRunDetail {
  const body = record(raw, 'twinRunDetail')
  return { run: decodeRunBody(body.run, 'twinRunDetail.run'), comparison: decodeComparison(body.comparison) }
}
